/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : labCarReducer.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 25-07-2024
 * @description Reducer function for handling lab car related actions and state changes.
 */
import actionType from "../actionTypes";

const initialState = {
  projectDetailsMindMap: [],
  projectDetails: [],
  eeComponentsList: [],
  EECount: 0,
  totalEECount: 0,
  eeComponentsMainList: [],
  totalEEMainCount: 0,
  eeComponentsLinkedList: [],
  totalEELinkedCount: 0,
  eeComponentsMainLinkedList: [],
  totalEEMainLinkedCount: 0,
  swModulesList: [],
  swModulesMainList: [],
  swModulesLinkedList: [],
  selectedProject: null,
  selectedVariant: null,
  // Vehicle Systems
  vehicleSystems: [],
  selectedVehicleSystemId: null,
  selectedVehicleSystemDetails: null,
  // Vehicle Components
  vehicleComponents: [],
  eeActionDetails: [],
};

const getPayloadData = (action) =>
  action.payload?.error ? [] : action.payload.data;

const getPayloadArray = (action) =>
  Array.isArray(action.payload?.data) ? action.payload.data : [];

const updateListState = (state, action, key, countKey) => ({
  ...state,
  [key]: getPayloadData(action),
  ...(countKey ? { [countKey]: action.payload.total_count } : {}),
});

const updateVehicleSystemsState = (state, action) => {
  const vehicleSystems = getPayloadArray(action);
  if (vehicleSystems.length === 0) {
    return {
      ...state,
      vehicleSystems,
      selectedVehicleSystemId: null,
      selectedVehicleSystemDetails: null,
      vehicleComponents: [],
    };
  }

  const isValidSelection = vehicleSystems.some(
    (vs) => vs.id === state.selectedVehicleSystemId,
  );

  return {
    ...state,
    vehicleSystems,
    selectedVehicleSystemId: isValidSelection
      ? state.selectedVehicleSystemId
      : vehicleSystems[0]?.id,
    selectedVehicleSystemDetails: isValidSelection
      ? state.selectedVehicleSystemDetails
      : vehicleSystems[0],
  };
};

const updateSelectedVehicleSystem = (state, action) => {
  const selectedVehicleSystem =
    Array.isArray(state.vehicleSystems) && state.vehicleSystems?.length > 0
      ? state.vehicleSystems.find(
          (system) =>
            parseInt(system?.id, 10) === parseInt(action.payload, 10),
        ) || state.vehicleSystems[0]
      : null;
  return {
    ...state,
    selectedVehicleSystemId: selectedVehicleSystem?.id ?? null,
    selectedVehicleSystemDetails: selectedVehicleSystem ?? null,
  };
};

const labCarReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionType.GET_PROJECT_DETAILS_MIND_MAP:
      return {
        ...state,
        projectDetailsMindMap: getPayloadData(action),
      };
    case actionType.GET_PROJECT_DETAILS:
      return {
        ...state,
        projectDetails: getPayloadData(action),
      };
    case actionType.GET_EE_COMPONENTS:
      console.log("---GET_EE_COMPONENTS---", action.payload.total_count);
      return updateListState(state, action, "eeComponentsList", "EECount");
    case actionType.GET_EE_COMPONENTS_COUNT:
      console.log("---GET_EE_COMPONENTS_COUNT---", action.payload.total_count);
      return {
        ...state,
        totalEECount: action.payload.total_count,
      };
    case actionType.GET_SW_MODULES:
      return updateListState(state, action, "swModulesList");
    case actionType.GET_MAIN_SW_MODULES:
      return updateListState(state, action, "swModulesMainList");
    case actionType.GET_LINKED_SW_MODULES:
      return updateListState(state, action, "swModulesLinkedList");
    case actionType.GET_EE_COMPONENTS_MAIN:
      return updateListState(
        state,
        action,
        "eeComponentsMainList",
        "totalEEMainCount",
      );
    case actionType.GET_EE_COMPONENTS_LINKED:
      return updateListState(
        state,
        action,
        "eeComponentsLinkedList",
        "totalEELinkedCount",
      );
    case actionType.GET_EE_COMPONENTS_MAIN_LINKED:
      return updateListState(
        state,
        action,
        "eeComponentsMainLinkedList",
        "totalEEMainLinkedCount",
      );
    case actionType.SET_SELECTED_PROJECT:
      return {
        ...state,
        selectedProject: action.payload,
      };
    case actionType.SET_SELECTED_VARIANT:
      return {
        ...state,
        selectedVariant: action.payload,
      };
    case actionType.RESET_LAB_CAR_DATA:
      return initialState;
    // Vehicle Systems
    case actionType.GET_VEHICLE_SYSTEMS_PR:
      return updateVehicleSystemsState(state, action);
    case actionType.SET_SELECTED_VEHICLE_SYSTEM:
      return updateSelectedVehicleSystem(state, action);
    // Vehicle Components
    case actionType.GET_VEHICLE_COMPONENTS:
      return {
        ...state,
        vehicleComponents: getPayloadArray(action),
      };
    case actionType.GET_EE_ACTIONS:
      return updateListState(state, action, "eeActionDetails");
    default:
      return state;
  }
};

export default labCarReducer;
